
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet, NavigationStart } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InstructionsComponent } from './components/instructions/instructions.component';
import { DOCUMENT } from '@angular/common';
import { VendorService } from './services/vendor.service';
import { HttpClientModule } from '@angular/common/http';
import { filter, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HttpClientModule],
  providers: [VendorService],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'PublicFacingRefferal';
  payorkey: number = 0;
  headerHtml: any = "";
  securityCode: string | null = '';
  isValidUrl: boolean = false;
  instructionText: any = "";;
  isInstruction: boolean = false;
  private processedSecurityCodes: Set<string> = new Set();
  private apiCallCounter: number = 0;
  private destroy$ = new Subject<void>();
  isCheckingUrl = true;

  private route = inject(ActivatedRoute);
  private router = inject(Router);


  constructor(
    private modalService: NgbModal,
    private vendorService: VendorService,
    private sanitizer: DomSanitizer,
  ) { 
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe((e: any) => {
        if (localStorage.getItem('isNewReferral') !== 'true') return;

        const url = (e.url || '').toLowerCase();
        const toFinal = url.endsWith('/final') || url === '/final' || url.endsWith('/#/final');
        if (!toFinal) return;

        const code = (sessionStorage.getItem('securityCode') || localStorage.getItem('lastSecurityCode') || '')
                      .trim().toLowerCase();
        if (code && code !== 'final') {
          this.router.navigate(['/', code], { replaceUrl: true });
        }
      });
  }

  ngOnInit(): void {
     this.setHeader();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.setHeader();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  private async init() {
    try {
      const child = this.getChild(this.route);
      const paramMap = await firstValueFrom(child.paramMap);
      const paramCode = (paramMap.get('code') || '').toLowerCase();
      const currentPath = (this.router.url.split('/')[1] || '').toLowerCase();

      let candidate;
      if (currentPath === 'final') {
        candidate = 'final';
      } else {
        candidate = paramCode || currentPath;
      }
      this.securityCode = candidate || sessionStorage.getItem('securityCode') || localStorage.getItem('lastSecurityCode') || '';
    } catch {
      const currentPath = (this.router.url.split('/')[1] || '').toLowerCase();
      this.securityCode = currentPath === 'final' ? 'final' : currentPath;
    }
  }

  async setHeader() {
    this.isCheckingUrl = true;
    const rawUrl = (this.router.url || '').toLowerCase();
    const firstSeg = rawUrl.split('/')[1] ?? '';
    if (!firstSeg) {
      this.headerHtml = '';
      this.isInstruction = false;
      this.instructionText = '';
      this.isValidUrl = false;
      this.isCheckingUrl = false;
      return;
    }

    await this.init();
    const key = (this.securityCode || '').trim().toLowerCase();
    const canCache = !!key && key !== 'final';

    if (sessionStorage.getItem('returningFromThanksCard') === 'true') {
      if (canCache) this.processedSecurityCodes.delete(key);
      sessionStorage.removeItem('returningFromThanksCard');
    }

    if (canCache && this.processedSecurityCodes.has(key)) {
      this.isValidUrl = true;
      this.isCheckingUrl = false;
      return;
    }

    if (key === 'final') {
      this.isValidUrl = true;
      this.isCheckingUrl = false;

      const sessionPayorkey = parseInt(sessionStorage.getItem('payorkey') ?? '0', 10);
      if (sessionPayorkey) {
        this.vendorService.getLeadHeaders(sessionPayorkey)
          .pipe(takeUntil(this.destroy$))
          .subscribe(lead => {
            this.headerHtml = lead?.HeaderText
              ? this.sanitizer.bypassSecurityTrustHtml(
                  lead.HeaderText.replace(/<img /g, '<img title="Header Image" alt="Header Image" ')
                )
              : '';
          });

        this.vendorService.getLeadInstructions(sessionPayorkey)
          .pipe(takeUntil(this.destroy$))
          .subscribe(instr => {
            this.isInstruction  = instr?.IsInstructionEnabled ?? false;
            this.instructionText = this.sanitizer.bypassSecurityTrustHtml(
              instr?.Instruction ? instr.Instruction.replace(/<img /g, '<img title="Header" ') : ''
            );
          });
      }
      return;
    }

    const internalRoutes = new Set([
      'agreement','summary','particpant','validateparticipant','service',
      'questions','provider','member','referal','witness',
      'nonenumeratedprovider','nonenumeratedorganization','nonenumeratedindividual'
    ]);

    if (!internalRoutes.has(key)) {
      this.vendorService.verifyReferralCode(key)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: confirm => {
            const valid = confirm != null;
            this.isValidUrl = valid;
            if (valid && canCache) this.processedSecurityCodes.add(key);
            this.isCheckingUrl = false;
          },
          error: () => {
            this.isValidUrl = false;
            this.isCheckingUrl = false;
          }
        });

      this.vendorService.getPayorIdByCode(key)
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          if (result != null) {
            this.payorkey = result;
            sessionStorage.setItem('payorkey', String(result));

            this.vendorService.getLeadHeaders(this.payorkey)
              .pipe(takeUntil(this.destroy$))
              .subscribe(lead => {
                this.headerHtml = (lead?.IsHeaderEnabled && lead?.HeaderText)
                  ? this.sanitizer.bypassSecurityTrustHtml(
                      lead.HeaderText.replace(/<img /g, '<img title="Header Image" alt="Header Image" ')
                    )
                  : '';
              });

            this.vendorService.getLeadInstructions(this.payorkey)
              .pipe(takeUntil(this.destroy$))
              .subscribe(instr => {
                this.isInstruction  = instr?.IsInstructionEnabled ?? false;
                this.instructionText = this.sanitizer.bypassSecurityTrustHtml(
                  instr?.Instruction ? instr.Instruction.replace(/<img /g, '<img title="Header" ') : ''
                );
              });
          }
        });

      return;
    }

    this.isValidUrl = true;
    const sessionPayorkey2 = parseInt(sessionStorage.getItem('payorkey') ?? '0', 10);
    if (sessionPayorkey2) {
      this.vendorService.getLeadHeaders(sessionPayorkey2)
        .pipe(takeUntil(this.destroy$))
        .subscribe(lead => {
          this.headerHtml = lead?.HeaderText
            ? this.sanitizer.bypassSecurityTrustHtml(
                lead.HeaderText.replace(/<img /g, '<img title="Header Image" alt="Header Image" ')
              )
            : '';
        });

      this.vendorService.getLeadInstructions(sessionPayorkey2)
        .pipe(takeUntil(this.destroy$))
        .subscribe(instr => {
          this.isInstruction  = instr?.IsInstructionEnabled ?? false;
          this.instructionText = this.sanitizer.bypassSecurityTrustHtml(
            instr?.Instruction ? instr.Instruction.replace(/<img /g, '<img title="Header" ') : ''
          );
        });
    }

    if (canCache) this.processedSecurityCodes.add(key);
    this.isCheckingUrl = false;
  }

  private getChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  openInstruction() {
    const result = this.modalService.open(InstructionsComponent, {
      size: 'lg',
      ariaLabelledBy: 'Instructions'
    });
    result.componentInstance.name = this.instructionText;
  }

}
